import { AwsUi } from '../../../core/runtime/src';
import BemClassBuilder from '../../../core/utils/src/class-builders/bem';
import { Button } from '../../button/src';
import { ColumnLayout } from '../../column-layout/src';
import { FormField } from '../../form-field/src';
import { defaultIsItemRemovableFunction } from './utils';
var SMALL_BREAKPOINT = 768;
export var AttributeEditor = AwsUi.__addComponent('awsui-attribute-editor', {
    releaseStatus: 'stable',
    generics: {
        Item: {
            defaultType: 'AttributeEditor.Item'
        }
    },
    regions: {
        // Displayed when there are no items to display.
        empty: {},
        // Displayed below the add button. Use it for additional information related to the attribute editor.
        additionalInfo: {}
    },
    properties: {
        // Text displayed in the add button element.
        addButtonText: {
            type: 'string'
        },
        // Text displayed in the remove button element.
        removeButtonText: {
            type: 'string'
        },
        // The items serve as the data source for all rows.
        // The display of a row is handled by the `definition` property.
        items: {
            type: 'array',
            itemTypes: ['Item']
        },
        // Function that determines whether an item is removable. When this function returns false, the remove
        // button is not rendered and the user will not be able to remove the item.
        // By default, all items are removable.
        isItemRemovable: {
            type: 'function',
            functionType: 'AttributeEditor.IsItemRemovableFunction',
            functionGenericType: 'Item',
            defaultValue: defaultIsItemRemovableFunction
        },
        // Specifies that the add button should be disabled.
        disableAddButton: {
            type: 'boolean'
        },
        // The editor configuration. Each object in the array represents one form field in the row.
        // * `label` [Renderable(isVisible)]: label of the form field.
        // * `errorText` [Renderable(item, itemIndex)]: text to show as a control validation message.
        //    It will render the form field as invalid if the returned value is not null or undefined.
        // * `hintText` [Renderable(item, itemIndex)]: text to show as a hint message below the field.
        // * `control` [Renderable(item, itemIndex)]: component to use as an input.
        //
        // A maximum of four fields are supported.
        //
        // **Renderable value**
        //   * **AngularJS:** Must be a string representing an Angular template or a function which returns such a string.
        //     For properties `errorText`, `hintText` and `control` you can also use local scope variables `$item` and `$itemIndex`.
        //     For the property `label` you can use variable `$isVisible`, see Development guidelines for usage of `isVisible` variable.
        //     **Security note:** the return value will be treated as HTML, make sure you have escaped the user input using
        //     [ngBind](https://docs.angularjs.org/api/ng/directive/ngBind) or [interpolation](https://docs.angularjs.org/guide/interpolation).
        //     Example: `'<input value="$item.prop"></input>' or ($item) => !$item.valid ? '<a>{{$item.prop}}</a>' : null`.
        //   * **React:** Must be a valid React element or a function that returns a React element to display.
        //     Example: `(item, index) => <input value={item.prop} />` or just `<b>Label<b>` in jsx.
        //   * **GWT:** Must be a lambda function that should return either a string, html element or GWT-widget instance.
        //     Example: `(item, index) -> 'some text'` or `(item, index) -> new HTMLPanel()`.
        definition: {
            type: 'array',
            itemTypes: [
                {
                    type: 'object',
                    objectType: 'AttributeEditor.FieldDefinition',
                    itemType: 'Item'
                }
            ]
        },
        // internally defined property describing the layout type
        __isMobile: {
            type: 'boolean'
        }
    },
    events: {
        // Fired when add button is clicked.
        addButtonClick: {
            bubbles: true,
            cancelable: false
        },
        // Fired when remove button is clicked.
        // The event `detail` contains the index of the corresponding item.
        removeButtonClick: {
            bubbles: true,
            cancelable: false,
            detailType: 'AttributeEditor.RemoveButtonClickDetail'
        }
    },
    elementQueries: [
        {
            read: function () { return window.innerWidth; },
            apply: function (width) {
                this.__isMobile = parseInt(width, 10) <= SMALL_BREAKPOINT;
            }
        }
    ],
    render: function (m, h) {
        var _this = this;
        var onItemAddHandler = function () {
            _this.__fireEvent('addButtonClick');
        };
        var onItemRemoveHandler = function (itemIndex) {
            _this.__fireEvent('removeButtonClick', { itemIndex: itemIndex });
        };
        var getRow = function (item, itemIndex) {
            var isFirstRow = itemIndex === 0;
            var isVisible = isFirstRow || _this.__isMobile;
            var primaryControl = (AwsUi.__createElement(ColumnLayout, { columns: _this.definition.length, content: AwsUi.__createElement("div", { "data-awsui-column-layout-root": "true" }, _this.definition.map(function (_a, controlIndex) {
                    var label = _a.label, control = _a.control, errorText = _a.errorText, hintText = _a.hintText;
                    var args = [{ $item: item }, { $itemIndex: itemIndex }];
                    return (AwsUi.__createElement(FormField, { label: _this.renderToMithril("attribute-label-" + itemIndex + "-" + controlIndex, label, [
                            { $isVisible: isVisible }
                        ]), key: controlIndex, errorText: errorText
                            ? _this.renderToMithril("attribute-error-" + itemIndex + "-" + controlIndex, errorText, args)
                            : undefined, hintText: hintText
                            ? _this.renderToMithril("attribute-hint-" + itemIndex + "-" + controlIndex, hintText, args)
                            : undefined, control: _this.renderToMithril("attribute-control-" + itemIndex + "-" + controlIndex, control, args), stretch: true, className: "awsui-attribute-editor__field" }));
                })) }));
            var secondaryControl = _this.isItemRemovable(item) && (AwsUi.__createElement(Button, { formAction: "none", text: _this.removeButtonText, click: function () { return onItemRemoveHandler(itemIndex); } }));
            return (AwsUi.__createElement("div", { className: "awsui-attribute-editor__row" },
                AwsUi.__createElement(FormField, { stretch: false, control: primaryControl, secondaryControl: secondaryControl })));
        };
        var getRows = function () {
            var rows = [];
            if (_this.items && _this.items.length > 0) {
                for (var i = 0; i < _this.items.length; i++) {
                    rows.push(getRow(_this.items[i], i));
                    rows.push(AwsUi.__createElement("div", { className: "awsui-attribute-editor__divider" },
                        AwsUi.__createElement("div", null)));
                }
            }
            return rows.length ? rows : h.region('empty');
        };
        var additionalInformationDisplay = function () {
            return (_this.additionalInfo && (AwsUi.__createElement("div", { className: new BemClassBuilder(_this, 'additional-info').toString() + " awsui-util-pt-xs" }, h.region('additionalInfo'))));
        };
        return (AwsUi.__createElement("div", { className: "awsui-util-spacing-v-s" },
            getRows(),
            AwsUi.__createElement("div", { className: "awsui-attribute-editor__add-row" },
                AwsUi.__createElement(Button, { click: onItemAddHandler, text: this.addButtonText, formAction: "none", disabled: this.disableAddButton }),
                additionalInformationDisplay())));
    }
});
//# sourceMappingURL=index.js.map